import { EventKind } from "@nova/schemas"
import { ActivityEvent, dayHeading } from "./activityCopy"
import { isSystemEvent } from "./activityIcons"

export const DEFAULT_RUN_WINDOW_MS = 30 * 60 * 1000

export const DEFAULT_RUN_COLLAPSE_THRESHOLD = 4

export const RUN_PREVIEW_COUNT = 10

export type GroupTimelineOptions = {
    now?: Date
    windowMs?: number
    collapseThreshold?: number
}

export type TimelineRun = {
    id: string
    createdBy: string
    events: ActivityEvent[]
    collapsible: boolean
    system: boolean
}

export type TimelineDay = {
    heading: string
    runs: TimelineRun[]
}

export const groupTimeline = (
    events: ActivityEvent[],
    {
        now = new Date(),
        windowMs = DEFAULT_RUN_WINDOW_MS,
        collapseThreshold = DEFAULT_RUN_COLLAPSE_THRESHOLD,
    }: GroupTimelineOptions = {}
) => {
    const days: TimelineDay[] = []

    for (const event of events) {
        if (!event.created_at) continue

        const heading = dayHeading(event.created_at, now)
        let day = days.at(-1)
        if (day?.heading !== heading) {
            day = { heading, runs: [] }
            days.push(day)
        }

        const run = day.runs.at(-1)
        const previous = run?.events.at(-1)?.created_at

        const continues =
            !!run &&
            !run.system &&
            !isSystemEvent(event) &&
            run.createdBy === event.created_by &&
            !!previous &&
            Math.abs(previous.getTime() - event.created_at.getTime()) <= windowMs

        if (continues) {
            run.events.push(event)
            run.collapsible = run.events.length >= collapseThreshold
        } else {
            day.runs.push({
                id: event.id,
                createdBy: event.created_by,
                events: [event],
                collapsible: 1 >= collapseThreshold,
                system: isSystemEvent(event),
            })
        }
    }

    return days
}

export type TimelineLine = { event: ActivityEvent; count: number }

const mergeKey = (event: ActivityEvent) =>
    event.kind === EventKind.CommentCreated ? event.id : `${event.kind}:${event.subject_type ?? ""}`

export const foldRunLines = (events: ActivityEvent[]) =>
    events.reduce<TimelineLine[]>((lines, event) => {
        const previous = lines.at(-1)
        if (previous && mergeKey(previous.event) === mergeKey(event)) previous.count++
        else lines.push({ event, count: 1 })
        return lines
    }, [])

export const visibleRunEvents = (run: TimelineRun, expanded: boolean) =>
    expanded || !run.collapsible ? run.events : run.events.slice(0, RUN_PREVIEW_COUNT)

export const hiddenRunCount = (run: TimelineRun, expanded: boolean) =>
    run.events.length - visibleRunEvents(run, expanded).length
